/**
 * Monitors and their alerts (ops insights Phase 6b, widened by Phase 7; CONTEXT.md "Monitor",
 * "Alert").
 *
 * The fixed lists here each have a SECOND COPY in a CHECK in migrations 0040 and 0043 (SQL cannot
 * import this file). Changing a list needs a migration that rewrites its CHECK, or the first
 * write of the new value is refused by the database. `packages/db/test/alerts-migration.test.ts`
 * writes every value of every list through the CHECKs, so the two cannot disagree unnoticed.
 *
 * What a monitor IS lives in code (this catalog, and the evaluator in `apps/api/src/monitors/`);
 * what a shop has CHOSEN for it (on or off, testing or live, thresholds) is its `monitors` row.
 */
import { z } from 'zod';
import { localClockOf, localWallTimeUtc, addDaysYmd } from './mcp/timezone';

/**
 * Every monitor the database admits. The last six are Phase 7's (spec §2): their catalog entries
 * are here, so the Settings write and the board read one definition; their evaluators come with
 * tickets 07–09, and the engine runs only the monitors it has an evaluator for (`MONITORS`).
 */
export const MONITOR_KEYS = [
  'van_negative',
  'ticket_unpicked',
  'reply_overdue',
  'part_flagged',
  'plan_visit_overdue',
  'request_unanswered',
  'quote_stale',
  'customer_lapsed',
  'job_short',
  'low_stock_needed',
] as const;
export type MonitorKey = (typeof MONITOR_KEYS)[number];

/**
 * Testing: alerts are recorded and shown to the owner only (Settings), and nobody is notified.
 * Live: on the board. The owner switches it (`monitor.update`, Phase 7), and the open alerts move
 * with it in the same transaction.
 */
export const MONITOR_MODES = ['testing', 'live'] as const;
export type MonitorMode = (typeof MONITOR_MODES)[number];

/** Hourly runs 7 AM–7 PM shop time; nightly at 6:15 AM shop time (`customer_lapsed`). */
export const MONITOR_CADENCES = ['hourly', 'nightly'] as const;
export type MonitorCadence = (typeof MONITOR_CADENCES)[number];

/**
 * What an alert is about. `stock` is a part AT a place (the only subject with a location, a
 * CHECK); every other subject is one row of its table, by id.
 */
export const ALERT_SUBJECT_TYPES = [
  'stock',
  'ticket',
  'agreement',
  'request',
  'quote',
  'customer',
  'job',
] as const;
export type AlertSubjectType = (typeof ALERT_SUBJECT_TYPES)[number];

/** The board's three columns (spec §6), in the order they are drawn. */
export const ALERT_CATEGORIES = ['neglect', 'parts', 'flag'] as const;
export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

export const ALERT_CATEGORY_LABEL: Record<AlertCategory, string> = {
  neglect: 'Neglected customers',
  parts: 'Missing parts',
  flag: 'Flagged issues',
};

/**
 * `clears_itself`: the first successful check that no longer finds the problem resolves it.
 * `needs_person`: only a person resolves it; a check never does.
 */
export const ALERT_CLASSES = ['clears_itself', 'needs_person'] as const;
export type AlertClass = (typeof ALERT_CLASSES)[number];

/** Most urgent first: the board sorts by this order. */
export const ALERT_BANDS = ['critical', 'high', 'normal'] as const;
export type AlertBand = (typeof ALERT_BANDS)[number];

/** True when `to` is more urgent than `from` (a band RISE clears a snooze, spec §3). */
export function bandRose(from: AlertBand, to: AlertBand): boolean {
  return ALERT_BANDS.indexOf(to) < ALERT_BANDS.indexOf(from);
}

export const ALERT_STATUSES = ['new', 'taken', 'resolved'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export const ALERT_RESOLVED_KINDS = ['auto', 'human'] as const;
export type AlertResolvedKind = (typeof ALERT_RESOLVED_KINDS)[number];

/**
 * An alert's history. The run writes `fired`, `updated`, `band_changed`, `auto_resolved`,
 * `unlatched` and (on a band rise) `unsnoozed`; a person writes the rest. `went_live` and
 * `went_testing` are the monitor's mode switch reaching each open alert.
 */
export const ALERT_EVENT_TYPES = [
  'fired',
  'updated',
  'auto_resolved',
  'taken',
  'human_resolved',
  'snoozed',
  'unsnoozed',
  'band_changed',
  'assigned',
  'unassigned',
  'unlatched',
  'muted',
  'unmuted',
  'escalated',
  'ticket_opened',
  'judged',
  'went_live',
  'went_testing',
] as const;
export type AlertEventType = (typeof ALERT_EVENT_TYPES)[number];

export const MONITOR_RUN_STATUSES = ['running', 'completed', 'failed'] as const;
export type MonitorRunStatus = (typeof MONITOR_RUN_STATUSES)[number];

// ── Thresholds (spec §2): one schema per monitor, read by its evaluator AND the Settings write ──
//
// Strict: a key a monitor does not know (a typo such as `minute`) is refused rather than silently
// run at the default. A monitor with nothing to tune takes `{}` and nothing else.

const wholeNumber = (min: number, max: number, fallback: number) =>
  z.number().int().min(min).max(max).default(fallback);

/** No setting. */
export const NO_THRESHOLDS = z.object({}).strict();

/**
 * "Ticket not picked up": `minutes` after it was raised (60). A week is the ceiling: past that,
 * "not picked up" is no longer a same-day signal.
 */
export const TICKET_UNPICKED_THRESHOLDS = z
  .object({ minutes: wholeNumber(1, 7 * 24 * 60, 60) })
  .strict();

/** "Plan visit not booked": `days` past the visit's date (7). */
export const PLAN_VISIT_OVERDUE_THRESHOLDS = z
  .object({ days: wholeNumber(1, 90, 7) })
  .strict();

/** "Request unanswered": `businessDays` after it came in (2; spec §5's business days). */
export const REQUEST_UNANSWERED_THRESHOLDS = z
  .object({ businessDays: wholeNumber(1, 30, 2) })
  .strict();

/** "Quote not followed up": `days` since it was sent or last followed up (14). */
export const QUOTE_STALE_THRESHOLDS = z
  .object({ days: wholeNumber(1, 180, 14) })
  .strict();

/** "Lapsed customer": `months` since the last completed visit (12, shop calendar months). */
export const CUSTOMER_LAPSED_THRESHOLDS = z
  .object({ months: wholeNumber(1, 60, 12) })
  .strict();

/** "Short for a booked job": visits starting today and the next `days` days (2). */
export const JOB_SHORT_THRESHOLDS = z.object({ days: wholeNumber(0, 14, 2) }).strict();

/** "Low stock needed soon": a visit within `days` days, today included (7). */
export const LOW_STOCK_NEEDED_THRESHOLDS = z
  .object({ days: wholeNumber(1, 30, 7) })
  .strict();

export interface MonitorCatalogEntry {
  label: string;
  /**
   * One plain sentence: when it fires, AT THE SHOP'S THRESHOLDS (already parsed by
   * `thresholds`). Never a default written into the words: the owner changes them in Settings.
   * Read it through `monitorDescription`, which the board and Settings share.
   */
  describe: (thresholds: Record<string, unknown>) => string;
  /** One plain sentence for the alert panel: what makes it go away on its own. */
  clearsWhen: string;
  /** The board column its alerts sit in. */
  category: AlertCategory;
  cadence: MonitorCadence;
  alertClass: AlertClass;
  defaultBand: AlertBand;
  subjectType: AlertSubjectType;
  /**
   * May this monitor's check fix a candidate's band over the shop's band override
   * (`bandLocked`)? Only where a RULING fixes the band, not a default the owner tunes:
   * `request_unanswered` (an emergency is critical, Phase 7 ruling B). A locked candidate from
   * any other monitor fails its run, as a stray evidence key does.
   */
  mayLockBand?: true;
  /**
   * The ONLY keys an alert's `evidence` may carry. Allow-listed so no message body or
   * customer contact detail can ride along into the alert record (spec §4).
   */
  evidenceKeys: readonly string[];
  /**
   * What the shop's `thresholds` may hold. The evaluator parses the row with it (a value it
   * cannot read is a refusal, Not monitored, never "nothing found"), and `monitor.update`
   * parses what the owner typed with the same one.
   */
  thresholds: z.ZodType<Record<string, unknown>>;
  /** What a new shop's row is seeded with: the schema's own defaults. */
  defaultThresholds: Record<string, unknown>;
}

/** "1 day", "7 days". */
const count = (n: unknown, unit: string): string =>
  `${n as number} ${unit}${n === 1 ? '' : 's'}`;

/** "45 minutes", "1 hour", "2 hours", "90 minutes". */
const minutesText = (n: unknown): string =>
  (n as number) % 60 === 0 ? count((n as number) / 60, 'hour') : count(n, 'minute');

/** A sentence that names no threshold (a monitor with none to tune). */
const fixed = (sentence: string) => () => sentence;

const TICKET_EVIDENCE = ['number', 'title', 'status'] as const;

type CatalogDraft = Omit<MonitorCatalogEntry, 'defaultThresholds'>;
const entry = (draft: CatalogDraft): MonitorCatalogEntry => ({
  ...draft,
  defaultThresholds: draft.thresholds.parse({}),
});

/**
 * ⚠️ The evidence keys of `plan_visit_overdue`, `customer_lapsed`, `job_short` and
 * `low_stock_needed` are PROVISIONAL (spec §5 names what each carries): the ticket that builds
 * the evaluator (08–09) owns them. `request_unanswered` and `quote_stale` are final (ticket 07),
 * pinned by `apps/api/test/monitor-request-quote.test.ts`. The lifecycle refuses any key a
 * candidate carries outside its entry's list.
 */
export const MONITOR_CATALOG: Record<MonitorKey, MonitorCatalogEntry> = {
  van_negative: entry({
    label: 'Van stock below zero',
    describe: fixed("A part's count in a tech's van, or a bin in it, is below zero."),
    clearsWhen: 'The count is back to zero or more.',
    category: 'parts',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'stock',
    evidenceKeys: ['item', 'location', 'quantity'],
    thresholds: NO_THRESHOLDS,
  }),
  ticket_unpicked: entry({
    label: 'Ticket not picked up',
    describe: (t) =>
      `A ticket is still New with no owner ${minutesText(t.minutes)} after it was raised.`,
    clearsWhen: 'Someone owns the ticket, it moves on from New, or it is resolved.',
    category: 'flag',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'ticket',
    evidenceKeys: TICKET_EVIDENCE,
    thresholds: TICKET_UNPICKED_THRESHOLDS,
  }),
  reply_overdue: entry({
    label: 'Customer reply overdue',
    describe: fixed(
      "A ticket's reply-due time has passed and nobody has written to the customer since.",
    ),
    clearsWhen:
      'Someone writes to the customer, the due time moves later, the ticket waits on the customer, or it is resolved.',
    category: 'flag',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'ticket',
    evidenceKeys: [...TICKET_EVIDENCE, 'replyDueAt'],
    thresholds: NO_THRESHOLDS,
  }),
  part_flagged: entry({
    label: 'Tech flags a missing part',
    describe: fixed('An open ticket a tech raised as "Missing or wrong part".'),
    clearsWhen: 'The ticket is resolved or moved to another category.',
    category: 'parts',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'ticket',
    evidenceKeys: TICKET_EVIDENCE,
    thresholds: NO_THRESHOLDS,
  }),
  plan_visit_overdue: entry({
    label: 'Plan visit not booked',
    describe: (t) =>
      `A service plan visit's date passed ${count(t.days, 'day')} ago and it wasn't done, skipped or moved later.`,
    clearsWhen:
      'Each overdue visit is done, rebooked or skipped, or the plan is paused, canceled or past its end.',
    category: 'neglect',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'agreement',
    // Final (ticket 08). A plan has no number, so its customer names it beside its name;
    // `overdueSince` is a shop date (YYYY-MM-DD), `overdueCount` the dates overdue.
    evidenceKeys: ['planName', 'customer', 'overdueSince', 'overdueCount'],
    thresholds: PLAN_VISIT_OVERDUE_THRESHOLDS,
  }),
  request_unanswered: entry({
    label: 'Request unanswered',
    describe: (t) =>
      `A request has waited ${count(t.businessDays, 'business day')} without becoming a quote or a job, or being declined.`,
    clearsWhen: 'The request is quoted, booked or declined.',
    category: 'neglect',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    // Critical when the request is an emergency (spec §2, ruling B): the evaluator's band, and
    // locked over the shop's override (`mayLockBand`).
    defaultBand: 'high',
    subjectType: 'request',
    mayLockBand: true,
    evidenceKeys: ['number', 'receivedAt', 'urgency'],
    thresholds: REQUEST_UNANSWERED_THRESHOLDS,
  }),
  quote_stale: entry({
    label: 'Quote not followed up',
    describe: (t) => `A sent quote has gone ${count(t.days, 'day')} with no follow-up.`,
    clearsWhen:
      'The quote is approved, declined, expired or revised, re-sent, or followed up (by hand or automatically).',
    category: 'neglect',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'normal',
    subjectType: 'quote',
    evidenceKeys: ['number', 'customer', 'sentAt', 'lastFollowUpAt'],
    thresholds: QUOTE_STALE_THRESHOLDS,
  }),
  customer_lapsed: entry({
    label: 'Lapsed customer',
    describe: (t) =>
      `A customer's last completed visit was more than ${count(t.months, 'month')} ago.`,
    clearsWhen:
      'They get a new completed visit, an open job, an upcoming visit or an active plan.',
    category: 'neglect',
    cadence: 'nightly',
    alertClass: 'clears_itself',
    defaultBand: 'normal',
    subjectType: 'customer',
    // Final (ticket 08): `lastVisitAt` is an ISO instant (the last check-out); format it on shop time.
    evidenceKeys: ['customer', 'lastVisitAt'],
    thresholds: CUSTOMER_LAPSED_THRESHOLDS,
  }),
  job_short: entry({
    label: 'Short for a booked job',
    describe: (t) =>
      `A job booked ${t.days === 0 ? 'for today' : `for today or the next ${count(t.days, 'day')}`} needs parts its techs' vans and the shop can't cover.`,
    clearsWhen:
      'The parts are covered, or the visit leaves the window, completes or is canceled.',
    category: 'parts',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'high',
    subjectType: 'job',
    // Final (ticket 09): the job number, its next live visit's start (ISO), up to 10 short lines
    // as ONE string ("Capacitor 45/5 × 2; Contactor 30A × 1": `projectEvidence` keeps plain
    // values only) and how many more lines are short.
    evidenceKeys: ['number', 'visitStartAt', 'shortLines', 'moreLines'],
    thresholds: JOB_SHORT_THRESHOLDS,
  }),
  low_stock_needed: entry({
    label: 'Low stock needed soon',
    describe: (t) =>
      `A part is below what a location keeps, and a job in the next ${count(t.days, 'day')} needs it.`,
    clearsWhen:
      'It is restocked or on order, the point is removed, or the job leaves the window.',
    category: 'parts',
    cadence: 'hourly',
    alertClass: 'clears_itself',
    defaultBand: 'normal',
    subjectType: 'stock',
    // Final (ticket 09): part and place names; on hand (rolled up), on order to that place, keep
    // at least and what jobs in the window still have to use, in units.
    evidenceKeys: ['item', 'location', 'onHand', 'onOrder', 'keepAtLeast', 'planned'],
    thresholds: LOW_STOCK_NEEDED_THRESHOLDS,
  }),
};

/**
 * When a monitor fires, in words, at the shop's `thresholds` (its `monitors` row). What the
 * schema cannot read (a row written by hand, or no row yet) reads at the monitor's defaults,
 * as the run would refuse it rather than guess.
 */
export function monitorDescription(key: MonitorKey, thresholds?: unknown): string {
  const entry = MONITOR_CATALOG[key];
  const parsed = entry.thresholds.safeParse(thresholds ?? {});
  return entry.describe(parsed.success ? parsed.data : entry.defaultThresholds);
}

// ── Thresholds as Settings shows them (spec §7): units from the key's name ─────────────────

/**
 * What one threshold counts in, named by its KEY (every monitor's schema uses one of these), so
 * Settings says "minutes" or "business days" without a second table of units per monitor.
 */
export const THRESHOLD_UNITS = {
  minutes: { one: 'minute', many: 'minutes' },
  days: { one: 'day', many: 'days' },
  businessDays: { one: 'business day', many: 'business days' },
  months: { one: 'month', many: 'months' },
} as const;
export type ThresholdName = keyof typeof THRESHOLD_UNITS;

export interface MonitorThresholdField {
  name: ThresholdName;
  unit: (typeof THRESHOLD_UNITS)[ThresholdName];
  min: number;
  max: number;
  /** The monitor's own default. */
  fallback: number;
}

/**
 * The numbers a monitor takes, read off its OWN schema (the one its evaluator and
 * `monitor.update` parse with), so the form's limits cannot drift from what is saved. Empty for
 * a monitor with nothing to tune ("when it happens").
 */
export function monitorThresholdFields(key: MonitorKey): MonitorThresholdField[] {
  const schema = MONITOR_CATALOG[key].thresholds;
  if (!(schema instanceof z.ZodObject)) {
    throw new Error(`${key}: a threshold schema must be an object`);
  }
  return Object.entries(schema.shape).map(([name, field]) => {
    if (!(name in THRESHOLD_UNITS)) {
      throw new Error(`${key}: threshold "${name}" has no unit (THRESHOLD_UNITS)`);
    }
    const withDefault = field as z.ZodDefault<z.ZodNumber>;
    const number = withDefault.unwrap();
    return {
      name: name as ThresholdName,
      unit: THRESHOLD_UNITS[name as ThresholdName],
      min: number.minValue ?? 0,
      max: number.maxValue ?? Number.MAX_SAFE_INTEGER,
      fallback: withDefault.def.defaultValue as number,
    };
  });
}

/** "1 day", "7 days", "1,440 minutes". */
export function thresholdText(field: Pick<MonitorThresholdField, 'unit'>, n: number) {
  return `${n.toLocaleString('en-US')} ${n === 1 ? field.unit.one : field.unit.many}`;
}

/**
 * Why `thresholds` is not a setting this monitor takes, in words a person reads under the box;
 * null when it is. ONE rule: the Settings form asks it before sending, and `monitor.update`'s
 * input refuses with the same sentence. The monitor's schema has the last word (a value it
 * refuses that the words did not catch still reads as a refusal).
 */
export function thresholdRefusal(
  key: MonitorKey,
  thresholds: Record<string, unknown>,
): string | null {
  const entry = MONITOR_CATALOG[key];
  const fields = monitorThresholdFields(key);
  for (const name of Object.keys(thresholds)) {
    if (!fields.some((f) => f.name === name)) {
      return fields.length === 0
        ? `${entry.label} has no number to set: it fires when it happens.`
        : `${entry.label} has no setting called "${name}".`;
    }
  }
  for (const f of fields) {
    const v = thresholds[f.name];
    if (v === undefined) continue;
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      return `Enter a number of ${f.unit.many}.`;
    }
    if (!Number.isInteger(v)) return `Enter a whole number of ${f.unit.many}.`;
    if (v < f.min || v > f.max) {
      return `Choose from ${thresholdText(f, f.min)} to ${thresholdText(f, f.max)}.`;
    }
  }
  return entry.thresholds.safeParse(thresholds).success
    ? null
    : `Not a setting ${entry.label} takes.`;
}

// ── The schedule (spec §2), in the shop's own time ─────────────────────────────────────────

/** The first and last local hours an hourly check runs in (7 AM and 7 PM, both included). */
export const MONITOR_FIRST_HOUR = 7;
export const MONITOR_LAST_HOUR = 19;
/** The nightly slot: the 06:15 local tick. */
export const MONITOR_NIGHTLY_HOUR = 6;
export const MONITOR_NIGHTLY_MINUTE = 15;
/** The sweep's UTC tick, every 15 minutes: each slot is the first tick inside its window. */
export const MONITOR_TICK_MINUTES = 15;

// ── Health: "Monitored" or "Not monitored", and why (spec §5) ──────────────────────────────

/** A run that concluded: what the latest one said. */
export interface MonitorConcludedRun {
  status: 'completed' | 'failed';
  startedAt: Date;
  /** The cause, for a failed run. */
  error: string | null;
}

export interface MonitorHealthInput {
  /** False when the shop turned it off. A monitor with no row yet is enabled and never ran. */
  enabled: boolean;
  cadence: MonitorCadence;
  /**
   * The latest run that is `completed` or `failed`, by start time. A `running` row is never
   * read: a run is one transaction, so no reader ever sees one (spec §5 amendment).
   */
  latestConcluded: MonitorConcludedRun | null;
  now: Date;
  /** The shop's IANA zone: "since the last 7 PM check" is a question about its wall clock. */
  timeZone: string;
}

/**
 * Why a monitor is Not monitored. There is no "checking" and no "stuck" (spec §5 amendment,
 * 2026-10-07): a run's `running` row lives inside its own transaction, so no reader ever sees
 * one, and a run that hangs or dies reads as `overdue`, which still fails closed.
 */
export const MONITOR_NOT_MONITORED_REASONS = [
  'disabled',
  'never_ran',
  'failed',
  'overdue',
] as const;
export type MonitorNotMonitoredReason = (typeof MONITOR_NOT_MONITORED_REASONS)[number];

export type MonitorHealth =
  | { state: 'monitored' }
  | { state: 'not_monitored'; reason: MonitorNotMonitoredReason; cause: string | null };

/** During the day, the latest completed check must be at most this old. */
export const MONITOR_HOURLY_FRESH_MS = 2 * 60 * 60_000;

/**
 * The earliest start a completed run may have and still count as recent.
 *
 * Hourly: within 2 hours from 8 AM to 7:59 PM; from 8 PM to 7:59 AM, since the last 7 PM check
 * (no check runs overnight, so "2 hours" would call every shop unmonitored all night). The 7
 * o'clock hour reads the overnight rule too: the check before 7 AM is the 7 PM one, and
 * "within 2 hours" there would reach back into the night and flag every shop until its 7 AM
 * run lands, while 8 AM through 7 PM already tolerate exactly one missed check.
 *
 * Nightly: since the last 06:15 check, where today's counts as due only once its tick window
 * has closed (06:30): between 06:15 and 06:29 the run may be in flight or not yet picked up, and
 * yesterday's check still stands.
 */
export function monitorFreshSince(
  cadence: MonitorCadence,
  now: Date,
  timeZone: string,
): Date {
  const clock = localClockOf(now, timeZone);
  if (cadence === 'nightly') {
    const nightlyMinutes = MONITOR_NIGHTLY_HOUR * 60 + MONITOR_NIGHTLY_MINUTE;
    const past = clock.hour * 60 + clock.minute >= nightlyMinutes + MONITOR_TICK_MINUTES;
    const day = past ? clock.date : addDaysYmd(clock.date, -1);
    return localWallTimeUtc(day, nightlyMinutes, timeZone);
  }
  if (clock.hour > MONITOR_FIRST_HOUR && clock.hour <= MONITOR_LAST_HOUR) {
    return new Date(now.getTime() - MONITOR_HOURLY_FRESH_MS);
  }
  const day = clock.hour > MONITOR_LAST_HOUR ? clock.date : addDaysYmd(clock.date, -1);
  return localWallTimeUtc(day, MONITOR_LAST_HOUR * 60, timeZone);
}

/**
 * Is this monitor producing verdicts? Pure, so the panel (ticket 03) and Phase 7's board read
 * one rule. Absence is the loud case: a monitor that never ran, or stopped, is Not monitored,
 * never "nothing found" (SONAR `detectorHealth.ts`'s lesson).
 *
 * Order: disabled; no concluded run; the latest concluded run failed (until a later one
 * completes); the latest completed run is not recent (`monitorFreshSince`). Otherwise Monitored.
 */
export function monitorHealth(input: MonitorHealthInput): MonitorHealth {
  const notMonitored = (
    reason: MonitorNotMonitoredReason,
    cause: string | null = null,
  ): MonitorHealth => ({ state: 'not_monitored', reason, cause });

  if (!input.enabled) return notMonitored('disabled');
  const last = input.latestConcluded;
  if (!last) return notMonitored('never_ran');
  if (last.status === 'failed') return notMonitored('failed', last.error);
  const freshSince = monitorFreshSince(input.cadence, input.now, input.timeZone);
  if (last.startedAt.getTime() < freshSince.getTime()) return notMonitored('overdue');
  return { state: 'monitored' };
}
